'use client';

import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { ProfileThemeType } from '../../__generated__/graphql';
import { generateCustomThemeClasses } from 'shared/misc/theme/custom/custom-theme-colors';
import { useAuth } from '../hooks/useAuth';

type ContextProps = {
  activeTheme: ProfileThemeType | null;
  setActiveTheme: React.Dispatch<React.SetStateAction<ProfileThemeType | null>>;
};

export const ThemeContext = React.createContext<ContextProps>({} as ContextProps);

export const useThemeContext = () => {
  return useContext(ThemeContext);
};

interface Props {
  children: ReactNode;
  theme: ProfileThemeType | null;
  customThemeColors: ThemeColors | null;
}

function CssInjector({ css }: { css: string }) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <style
      key="custom-theme-styles"
      dangerouslySetInnerHTML={{
        __html: css,
      }}
    />
  );
}

export const useAutoSetTheme = (
  syncTheme: ProfileThemeType | null | undefined | string,
  syncCustomThemeColors?: ThemeColors | null | undefined | string,
  isManual?: boolean
) => {
  const {
    activeTheme,
    setActiveTheme: setActiveThemeContext,
    customThemeColors,
    setCustomThemeColors,
  } = useContext(ThemeContext);

  const setActiveTheme = useCallback(
    (theme: ProfileThemeType, customThemeColorValues?: ThemeColors) => {
      setActiveThemeContext(theme);
      if (customThemeColorValues) {
        setCustomThemeColors(customThemeColorValues);
      }
    },
    [setActiveThemeContext, setCustomThemeColors],
  );

  useEffect(() => {
    if (
      !isManual &&
      (activeTheme !== syncTheme || (activeTheme === ProfileThemeType.Custom && customThemeColors))
    ) {
      setActiveTheme(syncTheme, syncCustomThemeColors);
    }
  }, [syncTheme, syncCustomThemeColors]);

  return setActiveTheme;
};

export function ThemeProvider({ children, theme, customThemeColors: initialCustomThemeColors }: Props) {
  const auth = useAuth();
  const [isClient, setIsClient] = useState(false);

  const [activeTheme, setActiveTheme] = useState<ProfileThemeType | null>(
    theme || auth.user?.profileTheme || null
  );

  const [customThemeColors, setCustomThemeColors] = useState<ThemeColors | null>(
    initialCustomThemeColors || null
  );

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    setCustomThemeColors(initialCustomThemeColors || null);
  }, [initialCustomThemeColors]);

  const memoizedValue = useMemo(
    () => ({ activeTheme, setActiveTheme, customThemeColors, setCustomThemeColors }),
    [activeTheme, customThemeColors, setCustomThemeColors]
  );

  const customThemeCss = useMemo(() => {
    return customThemeColors ? generateCustomThemeClasses(customThemeColors) : '';
  }, [customThemeColors]);

  return (
    <ThemeContext.Provider value={memoizedValue}>
      {isClient && customThemeColors && <CssInjector css={customThemeCss} />}
      {children}
    </ThemeContext.Provider>
  );
}
